import { Link } from 'react-router-dom';
import { BookOpen, FileText, ArrowRight, Clock } from 'lucide-react';
import { readingTime } from '../../data/insights';
import { TOPIC_ICONS } from './topicIcons';

const SubList = ({ topic, className = '' }) => (
  <ul className={`space-y-2.5 ${className}`}>
    {topic.subArticles.map((a) => (
      <li key={a.slug}>
        <Link to={`/insights/${topic.slug}/${a.slug}`} className="flex items-start gap-2.5 text-[0.95rem] text-ink/85 hover:text-purple transition-colors" data-testid={`topic-card-sub-link-${a.slug}`}>
          <FileText className="w-4 h-4 mt-1 flex-shrink-0 text-purple" />
          {a.title}
        </Link>
      </li>
    ))}
  </ul>
);

const MainLink = ({ topic }) => (
  <Link to={`/insights/${topic.slug}`} className="inline-flex flex-wrap items-center gap-2 text-purple font-display font-bold text-lg hover:gap-3 transition-all" data-testid={`topic-card-main-link-${topic.slug}`}>
    <BookOpen className="w-5 h-5" />
    Leitfaden lesen
    <span className="inline-flex items-center gap-1 text-ink/60 font-sans font-normal text-sm"><Clock className="w-4 h-4" />{readingTime(topic.mainArticle)} Min.</span>
    <ArrowRight className="w-5 h-5" />
  </Link>
);

export const TopicCard = ({ topic, featured = false }) => {
  const Icon = TOPIC_ICONS[topic.icon];

  if (featured) {
    return (
      <article className="card-ink card-ink--shadow grid lg:grid-cols-[5fr_7fr]" data-testid={`insights-topic-card-${topic.slug}`}>
        <Link to={`/insights/${topic.slug}`} className="relative min-h-[260px] lg:min-h-full overflow-hidden block border-b-2 lg:border-b-0 lg:border-r-2 border-ink bg-deep">
          <img src={topic.image} alt={`${topic.title} – Symbolbild`} width="600" height="400" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
          <span className="absolute top-5 left-5 badge-ink badge-ink--white">
            {Icon && <Icon className="w-4 h-4 text-purple" />}
            Einstieg
          </span>
        </Link>
        <div className="p-7 lg:p-10 flex flex-col">
          <span className="label-caps text-purple mb-3">Leitfaden</span>
          <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight mb-3">
            <Link to={`/insights/${topic.slug}`} className="hover:text-purple transition-colors">{topic.mainArticle.title}</Link>
          </h3>
          <p className="text-[1rem] leading-relaxed mb-5">{topic.teaser}</p>
          <MainLink topic={topic} />
          <div className="mt-6 pt-6 border-t border-ink/20">
            <p className="label-caps text-ink/60 mb-3">Vertiefungen</p>
            <SubList topic={topic} className="sm:grid sm:grid-cols-2 sm:gap-x-6 sm:space-y-0 sm:gap-y-2.5" />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="card-ink card-ink--hover flex flex-col" data-testid={`insights-topic-card-${topic.slug}`}>
      <Link to={`/insights/${topic.slug}`} className="relative h-48 overflow-hidden block border-b-2 border-ink bg-deep">
        <img src={topic.image} alt={`${topic.title} – Symbolbild`} width="600" height="400" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        {Icon && (
          <div className="absolute top-4 left-4 w-10 h-10 border-2 border-ink bg-off flex items-center justify-center">
            <Icon className="w-5 h-5 text-purple" />
          </div>
        )}
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display font-bold text-2xl leading-snug mb-3">{topic.title}</h3>
        <p className="text-[1rem] leading-relaxed mb-5 flex-1">{topic.teaser}</p>
        <MainLink topic={topic} />
        <SubList topic={topic} className="mt-5 pt-5 border-t border-ink/20" />
      </div>
    </article>
  );
};
